import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { Box, InputAdornment, useTheme } from "@mui/material";
import { Card, Form, message } from "antd";
import { InputOTP } from "antd-input-otp";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import ActionButton from "../../components/common/buttons/ActionButton";
import CancelButton from "../../components/common/buttons/CancelButton";
import InputField from "../../components/common/fields/InputField";
import PasswordField from "../../components/common/fields/PasswordField";
import SelectField from "../../components/common/fields/SelectField";
import Countdown from "../../components/common/Countdown";
import api from "../../hooks/api";
import useAuth from "../../hooks/useAuth";
import { useLanguages } from "../../hooks/useLanguages";
import { setCountry } from "../../store/reducers/country-reducer";
import { useChangeLanguage } from "../../hooks/useChangeLanguage";
import { pathAPI } from "../../utils/pathApi";
import LightScope from "../../theme/LightScope";

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [countdownTime, setCountdownTime] = useState(0);
  const [loadingOTP, setLoadingOTP] = useState(false);
  const [loadingResendOTP, setLoadingResendOTP] = useState(false);

  const { isLogin } = useAuth();
  const theme = useTheme();
  const [{ data: languages, isLoading }] = useLanguages();
  const [otpValues, setOtpValues] = useState<string[]>([]);
  const [sessionToken, setSessionToken] = useState<string>("");
  const dispatch = useDispatch();
  const changeLanguage = useChangeLanguage();
  const [form] = Form.useForm();
  const [loginForm] = Form.useForm();

  useEffect(() => {
    if (!languages || languages.length === 0) return;

    const currentSelected = loginForm.getFieldValue("language");
    if (!currentSelected) {
      const defaultLang: any =
        languages.find(
          (l: any) =>
            l.is_default === 1 ||
            l.is_default === true ||
            l.isDefault === true ||
            l.isDefault === 1 ||
            l.is_default_language === 1 ||
            l.is_default_language === true ||
            l.defaultLanguage === true ||
            l.default === true
        ) ||
        languages.find(
          (l: any) => l.language_code?.toLowerCase() === "en"
        ) ||
        languages[0];

      if (defaultLang) {
        loginForm.setFieldsValue({ language: defaultLang.id });
        changeLanguage(defaultLang.language_code);
      }
    }
  }, [languages, loginForm, changeLanguage]);

  useEffect(() => {
    if (isLogin) navigate("/", { replace: true });
  }, [isLogin, navigate]);

  const handleOnChange = (value: any) => {
    const currentLang: any = languages?.filter((t) => t.id === value);
    if (currentLang && currentLang[0]) {
      changeLanguage(currentLang[0].language_code);
    }
  };

  const onFinish = (values: any) => {
    if (!values.email || !values.password) {
      message.error(t("login.error.missing_credentials"));
    } else {
      setLoading(true);
      api
        .POST(pathAPI.login, { ...values })
        .then((data) => {
          setShowOTP(true);
          setSessionToken((data as any)?.data?.session);
        })
        .catch((e) => message.error(e.message || t("login.error.password_wrong")))
        .finally(() => setLoading(false));
    }
  };

  const resendOTPHanlde = () => {
    setLoadingResendOTP(true);

    // Clear OTP input and error messages
    setOtpValues([]);
    form.setFieldValue("otp", []);
    form.setFields([
      {
        name: "otp",
        errors: [],
      },
    ]);

    api
      .POST(pathAPI.resendOTP, { session: sessionToken })
      .then((data) => {
        setSessionToken((data as any)?.data?.session);
        setCountdownTime(30);
        message.success(`${t("mess.resend_email_success")}`);
      })
      .catch((e) => message.error(e.message))
      .finally(() => setLoadingResendOTP(false));
  };

  const handleFinishOTP = (values: any) => {
    // The value will be array of string
    // Check the field if there is no value, or value is undefined/empty string
    setLoadingOTP(true);
    const { otp } = values;
    if (!otp || otp.includes(undefined) || otp.includes("") || otp.length < 6) {
      setLoadingOTP(false);
      return form.setFields([
        {
          name: "otp",
          errors: [t("login.otp.invalid")],
        },
      ]);
    }

    api
      .POST(pathAPI.verify, {
        session: sessionToken,
        token: `${otp}`.split(",").join(""),
      })
      .then((data) => {
        localStorage.setItem("token", (data as any)?.data?.access_token);
        localStorage.setItem("expires_in", (data as any)?.data.expires_in);
        
        localStorage.setItem(
          "email",
          ((data as any)?.data?.user &&
            ((data as any)?.data.user as any)?.email) ||
            ""
        );
        localStorage.setItem(
          "avatar",
          ((data as any)?.data?.user &&
            ((data as any)?.data.user as any)?.avatar) ||
            ""
        );
        localStorage.setItem(
          "current_user_id",
          ((data as any)?.data?.user &&
            ((data as any)?.data.user as any)?.id) ||
            ""
        );
        localStorage.setItem(
          "current_user_role",
          ((data as any)?.data?.user &&
            ((data as any)?.data.user as any)?.role.name) ||
            ""
        );

        if (window.history.state && window.history.state.idx > 2) {
          navigate(-1);
        } else {
          navigate("/");
        }
        // GET AND SET COUNTRIES to localStorage and store
        api
          .GET(pathAPI.countries, {}, {}, true)
          .then((data) => {
            dispatch(setCountry(data.data));
            localStorage.setItem("countries", JSON.stringify(data.data));
          })
          .catch((e) => message.error(e.message));
      })
      .catch((e) => {
        return form.setFields([
          {
            name: "otp",
            errors: [t("login.otp.invalid")],
          },
        ]);
      })
      .finally(() => setLoadingOTP(false));
  };

  if (showOTP) {
    return (
      <LightScope>
      <div className="flex justify-center items-center h-screen bg-pink-200">
        <div className="w-[400px] shadow-xl rounded-[20px]">
          <Card
            title={t("login.title")}
            className="[&_.ant-card-bordered]:border-none [&_.ant-card-bordered]:rounded-[10px] [&_.ant-card-head]:rounded-t-[10px] [&_.ant-card-head]:bg-[#ba2156] [&_.ant-card-head]:text-white [&_.ant-card-head]:text-center [&_.ant-select-selector]:h-10 [&_.ant-select-selector]:text-left [&_.ant-form-item-control-input-content]:text-center [&_.center-error-message_.ant-form-item-explain-error]:text-center [&_.center-error-message_.ant-form-item-explain-error]:py-[5px]"
          >
            <Box className="p-2 text-xs text-center border border-[#ba2156] text-[#ba2156] rounded-lg mb-4">
              {t("login.otp.content")}
            </Box>

            <Form form={form} onFinish={handleFinishOTP}>
              <Form.Item
                name="otp"
                className="center-error-message"
                rules={[{ validator: async () => Promise.resolve() }]}
              >
                <InputOTP
                  autoFocus
                  onChange={setOtpValues}
                  value={otpValues}
                  inputType="numeric"
                  length={6}
                />
              </Form.Item>

              <Form.Item noStyle>
                <ActionButton appSize="large" fullWidth type="submit" loading={loadingOTP}>
                  {t("btn.submit")}
                </ActionButton>
              </Form.Item>
            </Form>
            <Box className="flex justify-end text-right mt-2">
              <CancelButton
                onClick={resendOTPHanlde}
                disabled={countdownTime > 0 || loadingResendOTP}
              >
                {t("btn.resend_otp")}
                {countdownTime > 0 && (
                  <Countdown time={countdownTime} setTime={setCountdownTime} />
                )}
              </CancelButton>
            </Box>
          </Card>
        </div>
      </div>
      </LightScope>
    );
  }

  return (
    <LightScope>
    <div className="flex justify-center items-center h-screen bg-pink-200">
      <div className="w-[400px] shadow-xl rounded-[20px]">
        <Card
          title={t("login.title")}
          className="[&_.ant-card-bordered]:border-none [&_.ant-card-bordered]:rounded-[10px] [&_.ant-card-head]:rounded-t-[10px] [&_.ant-card-head]:bg-[#ba2156] [&_.ant-card-head]:text-white [&_.ant-card-head]:text-center [&_.ant-select-selector]:h-10 [&_.ant-select-selector]:text-left [&_.ant-form-item-control-input-content]:text-center"
        >
          <Form
            form={loginForm}
            name="normal_login"
            className="login-form"
            initialValues={{ remember: true }}
            onFinish={onFinish}
          >
            <Form.Item
              name="email"
              rules={[{ required: true, message: `${t("mess.input_email")}` }]}
            >
              <InputField
                startAdornment={
                  <InputAdornment position="start">
                    <UserOutlined className="site-form-item-icon" />
                  </InputAdornment>
                }
                placeholder={t("txt_email")}
              />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[
                { required: true, message: `${t("mess.input_password")}` },
              ]}
            >
              <PasswordField
                startAdornment={
                  <InputAdornment position="start">
                    <LockOutlined className="site-form-item-icon" />
                  </InputAdornment>
                }
                placeholder={t("txt_password")}
              />
            </Form.Item>
            <Form.Item
              name="language"
              rules={[{ required: true, message: `${t("mess.required")}` }]}
            >
              <SelectField
                onChange={(value) => handleOnChange(value)}
                placeholder={t("txt_select_language")}
                loading={isLoading}
                options={(languages || []).map((i) => ({
                  value: i.id,
                  label: `${i.name} (${i.language_code})`,
                  icon: i.language_icon,
                }))}
              />
            </Form.Item>

            <Form.Item>
              <ActionButton appSize="large" type="submit" loading={loading} sx={{ width: "50%" }}>
                {t("btn.login")}
              </ActionButton>
            </Form.Item>
          </Form>
          <div className="flex justify-between">
            <Link className="text-[#ba2156] hover:text-[#9a1a46]" to="/forgot-password">
              {t("txt_forgot_password")}
            </Link>
          </div>
        </Card>
      </div>
    </div>
    </LightScope>
  );
};
export default Login;
